/**
 * Convenience helpers for `contact_tasks.entity_type` / `entity_id`.
 * Canonical home (promoted from apps/hub/lib/domain/task-entity-linkage.ts,
 * which re-exports from here so existing hub imports keep working).
 *
 * **Generic polymorphic links are unchanged.** Any row that already has an explicit
 * `entity_type` + `entity_id` pair (deals, communications, time_entries, offerings, etc.)
 * is returned as-is — we never overwrite those.
 *
 * **Partial polymorphic fields:** If only one of `entity_type` / `entity_id` is set, we
 * do not infer the other from FK columns — avoids fighting bespoke flows or incomplete saves.
 *
 * **FK backfill (when both polymorphic fields are empty):** If the task has no explicit
 * polymorphic pair yet, we derive one from relational columns only:
 * `ticket_id` → tickets, else `contact_id` → contacts, else `company_id` → companies.
 * Priority when several FKs are set: ticket → contact → company (most specific wins).
 */
function normalizeUuid(value) {
    if (value == null)
        return null;
    const t = String(value).trim();
    return t.length > 0 ? t : null;
}
/** Derive polymorphic pair from FK columns only — not used when entity_type/entity_id are explicit. */
export function deriveTaskPrimaryEntityLink(params) {
    const ticketId = normalizeUuid(params.ticket_id);
    const contactId = normalizeUuid(params.contact_id);
    const companyId = normalizeUuid(params.company_id);
    if (ticketId) {
        return { entity_type: "tickets", entity_id: ticketId };
    }
    if (contactId) {
        return { entity_type: "contacts", entity_id: contactId };
    }
    if (companyId) {
        return { entity_type: "companies", entity_id: companyId };
    }
    return null;
}
export function applyTaskPrimaryEntityFields(row) {
    const explicitType = row.entity_type != null ? String(row.entity_type).trim() : "";
    const explicitId = row.entity_id != null ? String(row.entity_id).trim() : "";
    const hasExplicitLink = explicitType.length > 0 && explicitId.length > 0;
    if (hasExplicitLink) {
        return row;
    }
    const hasPartialPolymorphicLink = (explicitType.length > 0 && explicitId.length === 0) ||
        (explicitType.length === 0 && explicitId.length > 0);
    if (hasPartialPolymorphicLink) {
        return row;
    }
    const link = deriveTaskPrimaryEntityLink({
        ticket_id: row.ticket_id,
        contact_id: row.contact_id,
        company_id: row.company_id,
    });
    if (!link) {
        return row;
    }
    return {
        ...row,
        entity_type: link.entity_type,
        entity_id: link.entity_id,
    };
}
/** Normalize legacy singular ticket entity_type values for comparisons. */
export function taskRowTicketEntityId(entityType, entityId) {
    if (entityId == null || String(entityId).trim().length === 0)
        return null;
    const t = entityType?.trim().toLowerCase() ?? "";
    if (t === "ticket" || t === "tickets") {
        return String(entityId).trim();
    }
    return null;
}
/** Deal UUID when polymorphic link points at deals (singular alias supported). */
export function taskRowDealEntityId(entityType, entityId) {
    if (entityId == null || String(entityId).trim().length === 0)
        return null;
    const t = entityType?.trim().toLowerCase() ?? "";
    if (t === "deal" || t === "deals") {
        return String(entityId).trim();
    }
    return null;
}
